<template>
  <form
    class="search-form"
    @submit.prevent="onSubmit"
  >
    <div
      class="tw:flex tw:flex-nowrap tw:items-center tw:justify-between tw:p-4"
    >
      <div
        class="tw:flex tw:min-h-14.25 tw:min-w-0 tw:flex-1 tw:flex-nowrap tw:items-center tw:pr-4 tw:rounded-xs tw:bg-denim-100"
      >
        <UniversalSearchDropdown
          v-if="showSearchScope"
          v-model="searchScope"
          @update:model-value="$emit('search')"
        />
        <BaseInputText
          ref="input"
          v-model="searchQuery"
          borderless
          class="tw:flex tw:min-w-0 tw:flex-auto tw:flex-col tw:justify-center tw:rounded-none tw:self-stretch tw:[--s-input-default-bg:transparent]"
          clearable
          data-testid="universal-search-input"
          :input-classes="[
            'tw:text-ellipsis',
            showSearchScope ? 'tw:p-0' : 'tw:px-6',
          ]"
          :label="searchPlaceholder"
          label-class="tw:sr-only"
          new-design
          :placeholder="searchPlaceholder"
          size="12"
          @focus="onFocus"
          @update:model-value="onInput"
        />
      </div>
      <button
        class="tw:ml-2 tw:cursor-pointer tw:border-0 tw:bg-transparent tw:text-[16px] tw:text-inherit tw:hover:text-[#444] tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary tw:rounded-sm tw:p-2"
        data-testid="universal-search-cancel-button"
        type="button"
        @click="close"
      >
        {{ $t("global.cancel") }}
      </button>
    </div>
  </form>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseInputText } from "shared/components/base";
import { useUserStore } from "shared/stores/user";

import UniversalSearchDropdown from "./UniversalSearchDropdown";

export default {
  name: "UniversalSearchInput",
  components: {
    BaseInputText,
    UniversalSearchDropdown,
  },
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    scope: {
      type: String,
      required: true,
    },
  },
  emits: [
    "update:scope",
    "close",
    "clear",
    "focus",
    "input",
    "update:modelValue",
    "search",
  ],
  setup() {
    const userStore = useUserStore();
    const { adminUserEnabled, customerViewEnabled } = storeToRefs(userStore);

    return {
      adminUserEnabled,
      customerViewEnabled,
    };
  },
  data() {
    return {
      searchQuery: "",
      lastSearchQuery: "",
    };
  },
  computed: {
    canSearchAvailableContent() {
      if (this.adminUserEnabled && !this.customerViewEnabled) return true;

      return (
        this.$features.has("has_search_available_content") &&
        this.$permissions.has("can_search_available_content")
      );
    },
    showSearchScope() {
      if (this.canSearchAvailableContent) {
        return true;
      }

      return !this.$features.has("has_outreach");
    },
    searchPlaceholder() {
      return this.scope === "streams" && this.$isDesktop
        ? this.$t("universal_search_input.search_coverage")
        : this.$t("global.search");
    },
    searchScope: {
      get() {
        return this.scope;
      },
      set(value) {
        this.$emit("update:scope", value);
      },
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler() {
        this.searchQuery = this.modelValue;
      },
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    async focus() {
      await this.$nextTick();
      // Clearing the query refocuses the field, so the search can be closed
      this.$refs.input?.focus();
    },
    clearSearchQuery() {
      this.searchQuery = "";
      this.$emit("clear");
      this.$emit("input", "");
      this.focus();
    },
    onFocus() {
      this.$emit("focus");
    },
    onInput() {
      if (!this.searchQuery.length) {
        this.clearSearchQuery();

        return;
      }

      this.$emit("focus");
      this.$emit("input", this.searchQuery);
    },
    onSubmit() {
      if (
        this.searchQuery.length &&
        this.searchQuery !== this.lastSearchQuery
      ) {
        this.$emit("update:modelValue", this.searchQuery);
        this.$emit("search");
        this.lastSearchQuery = this.searchQuery;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-form {
  container: universal-search-bar / inline-size;
}
</style>
